import _sortBy from 'lodash/sortBy';

import sdk from '../../sdk';
import * as utils from '../../shared/utils';
import l18n from '../../shared/strings';
import Logger from '../../services/Logger';
import TYPES from '../action-types';
import Toast from '../../components/Toast';
import { showErrorDialog, showDialog } from '../../components/dialog';
import { reloadCurrentPage } from '../../helpers/history';
import { filterItemsByScope } from '../helpers/productAttributes';

export const get = () => async (dispatch) => {
  try {
    dispatch({ type: TYPES.PRODUCT_ATTRIBUTES.FETCH.START });

    const { data: response } = await sdk.productAttributes.getSchema();

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.FETCH.COMPLETE,
      payload: {
        items: response.fields,
        schemaWarnings: response.schemaWarnings || [],
      },
    });
  } catch (error) {
    Logger.log('UI', 'ToastProductAttributesNotLoaded');
    Toast(
      'Product attributes have not loaded. Try refreshing the page to see them.',
      {
        autoClose: false,
        type: 'error',
      },
    );
    dispatch({ type: TYPES.PRODUCT_ATTRIBUTES.FETCH.FAILED, error });
  }
};

export const add = (data, quiet) => async (dispatch) => {
  try {
    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.ADD.START,
      payload: { item: data },
    });

    if (!quiet) await sdk.productAttributes.create(data);

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.ADD.COMPLETE,
      payload: { item: data },
    });
  } catch (error) {
    const errorMessage = utils.getDataFromResponceError(error, 'msg') || l18n.CUSTOMFIELDSSCHEMA.textErrorDbCant;
    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.ADD.FAILED,
      payload: { item: data },
      error: errorMessage,
    });
  }
};

export const removeErrorMessage = () => (dispatch) => {
  dispatch({
    type: TYPES.PRODUCT_ATTRIBUTES.REMOVE_ERROR,
  });
};

export const remove = (title, scope, force = false) => async (dispatch, getState) => {
  try {
    const state = getState();
    const newItems = state.productAttributes.items;

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.REMOVE.START,
      payload: { title, scope },
    });

    await sdk.productAttributes.delete(title, scope, force);

    const itemToRemove = newItems.find((item) => item.title === title && item.scope === scope);
    if (itemToRemove) {
      const index = itemToRemove.order;
      newItems.splice(index, 1);
      newItems.forEach((field) => {
        if (field.scope === scope && field.order > itemToRemove.order) {
          field.order--;
        }
      });
      _sortBy(newItems, ['scope', 'order']);
    }

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.REMOVE.COMPLETE,
      payload: { title, scope },
    });
  } catch (error) {
    Logger.error(new Error('Can not remove product attribute'), { error, showDialog: true }, [
      'showWriteToSupportDialog',
      { errorMessage: (error && error.message) || 'NoMessage' },
    ]);

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.REMOVE.FAILED,
      payload: { title, scope },
      error,
    });
  }
};

export const update = (titles, key, value, scope) => async (dispatch, getAll) => {
  try {
    const customFields = getAll().productAttributes.items.filter(
      (item) => item.scope === scope && titles.includes(item.title),
    );
    customFields.forEach((field) => {
      // eslint-disable-next-line no-param-reassign
      field[key] = value;
    });

    dispatch({ type: TYPES.PRODUCT_ATTRIBUTES.UPDATE.START, payload: { titles, scope } });

    await sdk.productAttributes.update(customFields);

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.UPDATE.COMPLETE,
      payload: {
        titles, key, value, scope,
      },
    });
  } catch (error) {
    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.UPDATE.FAILED,
      payload: { titles, scope },
      error,
    });
  }
};

export const updateCustomField = (customFieldModified, force = false) => async (dispatch) => {
  try {
    // eslint-disable-next-line no-param-reassign
    delete customFieldModified.defautlTitle;

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.START,
      payload: { customField: customFieldModified },
    });

    try {
      await sdk.productAttributes.update([customFieldModified], force);
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg');
      const errorSubcode = utils.getDataFromResponceError(error, 'subcode');
      if (errorSubcode === 'CustomFieldsTypeChangedApiError' || errorSubcode === 'CustomFieldsTypeChangedAssetsLimitApiError') {
        let l10n = l18n.CUSTOMFIELDSSCHEMA.changeTypeWarningDialog;
        let onOk = () => updateCustomField(customFieldModified, true)(dispatch);
        const onCancel = () => {
          dispatch({
            type: TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.CANCELLED,
            payload: { customField: customFieldModified },
          });
        };

        if (errorSubcode === 'CustomFieldsTypeChangedAssetsLimitApiError') {
          l10n = l18n.CUSTOMFIELDSSCHEMA.changeTypeLimitDialog;
          onOk = () => {
            window.dispatchEvent(new Event('toolbar:ui:liveSupport'));
          };
        }
        const { title, text, textBtnOk } = l10n;
        showDialog({
          title, text, textBtnOk, onOk, onCancel,
        });
        return 'confirmation_required';
      }
      showErrorDialog(errorMessage);
      throw error;
    }

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.COMPLETE,
      payload: { customField: customFieldModified },
    });
    return 'success';
  } catch (error) {
    Logger.error(new Error('Can not update product attribute'), { error }, [
      'ProductAttributeUpdateFailed',
      { errorMessage: (error && error.message) || 'NoMessage' },
    ]);
    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.FAILED,
      payload: { customField: customFieldModified },
      error,
    });
    return 'failed';
  }
};

export const move = (titlesToMove, fieldTitleToMoveAfter, scope) => async (dispatch, getAll) => {
  try {
    dispatch({ type: TYPES.PRODUCT_ATTRIBUTES.MOVE.START, payload: { scope } });

    let scopeItems = [...filterItemsByScope(getAll().productAttributes.items, scope)];
    const indexToRemove = scopeItems.findIndex((item) => item.title === titlesToMove[0]);
    const itemsToMove = scopeItems.splice(indexToRemove, titlesToMove.length);

    const indexToPut = fieldTitleToMoveAfter
      ? scopeItems.findIndex((item) => item.title === fieldTitleToMoveAfter)
      : -1;

    scopeItems.splice(indexToPut + 1, 0, ...itemsToMove);
    scopeItems = scopeItems.map((item, index) => {
      const { isUpdating, ...data } = item; // eslint-disable-line
      data.order = index;
      return data;
    });

    await sdk.productAttributes.update(scopeItems);

    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.MOVE.COMPLETE,
      payload: { items: scopeItems, scope },
    });
  } catch (error) {
    const errorMessage = utils.getDataFromResponceError(error, 'msg');
    if (errorMessage) {
      Toast(errorMessage, { autoClose: false });
    } else {
      Toast(l18n.CUSTOMFIELDSSCHEMA.textMoveFieldError, {
        autoClose: false, type: 'error', onOk: reloadCurrentPage, btnOkValue: l18n.HISTORY.textBtnRefresh,
      });
    }
    dispatch({ type: TYPES.PRODUCT_ATTRIBUTES.MOVE.FAILED, error });
  }
};

export function applySearch(value, scope) {
  return (dispatch) => {
    dispatch({
      type: TYPES.PRODUCT_ATTRIBUTES.SEARCH,
      payload: { value, scope },
    });
  };
}
